import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PipelineTemplate } from '../../api/structs';

export interface PipelineSelectExtraEntry {
  value: string;
  label: string;
}

export interface PipelineSelectProps {
  pipelines: PipelineTemplate[];
  value: string;
  onChange: (value: string) => void;
  /** Narrows which pipelines are offered. Applied before grouping/sorting — it
   *  never changes what a caller's own filter already decided, only lets
   *  PipelineSelect own the grouping/order/search on top of it. */
  filter?: (pipeline: PipelineTemplate) => boolean;
  /** Non-pipeline entries (e.g. "New Pipeline") rendered above the groups,
   *  ungrouped, in the order given. */
  extraEntries?: PipelineSelectExtraEntry[];
  placeholder?: string;
  selectSize?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
  /** Search box appears once (extras + pipelines) exceeds this count. */
  searchThreshold?: number;
}

/** Built-in ("System") pipelines are the ones with no owning user — mirrors
 *  the split BuilderView/Series/PipelineList each hand-rolled
 *  before this component existed. */
const isBuiltIn = (p: PipelineTemplate): boolean => !p.user_id || p.user_id.trim() === '';

/** Case-insensitive by name, tie-broken by id — same determinism contract as
 *  the backend's sortPipelineTemplates, so the UI never contradicts it. */
const byNameThenId = (a: PipelineTemplate, b: PipelineTemplate): number => {
  const nameCompare = (a.name || a.id).localeCompare(b.name || b.id, undefined, {
    sensitivity: 'base',
  });
  if (nameCompare !== 0) return nameCompare;
  return a.id.localeCompare(b.id);
};

interface FlatOption {
  key: string;
  value: string;
  label: string;
  group?: string;
}

const PipelineSelect: React.FC<PipelineSelectProps> = ({
  pipelines,
  value,
  onChange,
  filter,
  extraEntries = [],
  placeholder = 'Select pipeline...',
  selectSize = 'md',
  className = '',
  ariaLabel,
  searchThreshold = 8,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const scoped = useMemo(() => (filter ? pipelines.filter(filter) : pipelines), [pipelines, filter]);

  const builtIns = useMemo(() => scoped.filter(isBuiltIn).sort(byNameThenId), [scoped]);
  const mine = useMemo(() => scoped.filter((p) => !isBuiltIn(p)).sort(byNameThenId), [scoped]);

  const totalCount = extraEntries.length + builtIns.length + mine.length;
  const showSearch = totalCount > searchThreshold;

  const query = search.trim().toLowerCase();
  const matchesQuery = (label: string): boolean => !query || label.toLowerCase().includes(query);

  const visibleExtras = showSearch ? extraEntries.filter((e) => matchesQuery(e.label)) : extraEntries;
  const visibleBuiltIns = showSearch ? builtIns.filter((p) => matchesQuery(p.name || p.id)) : builtIns;
  const visibleMine = showSearch ? mine.filter((p) => matchesQuery(p.name || p.id)) : mine;

  const flatOptions: FlatOption[] = [
    ...visibleExtras.map((e) => ({ key: `extra-${e.value}`, value: e.value, label: e.label })),
    ...visibleBuiltIns.map((p) => ({
      key: `builtin-${p.id}`,
      value: p.id,
      label: p.name || p.id,
      group: 'Built-in',
    })),
    ...visibleMine.map((p) => ({
      key: `mine-${p.id}`,
      value: p.id,
      label: p.name || p.id,
      group: 'My pipelines',
    })),
  ];

  const selectedLabel = useMemo(() => {
    const extra = extraEntries.find((e) => e.value === value);
    if (extra) return extra.label;
    const found = scoped.find((p) => p.id === value);
    return found ? found.name || found.id : '';
  }, [extraEntries, scoped, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    if (showSearch) {
      // Focus after the dropdown paints so typing lands in the box immediately.
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [isOpen, showSearch]);

  const sizeClass = selectSize === 'sm' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm';

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const optionButtonClass = (isSelected: boolean, indent: boolean) =>
    `w-full text-left ${indent ? 'px-4' : 'px-3'} py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--surface-3)] transition-colors ${
      isSelected ? 'text-[var(--accent-strong)] bg-[var(--surface-1)]' : 'text-[var(--app-text)]'
    }`;

  const renderGroup = (label: string, options: PipelineTemplate[]) => {
    if (options.length === 0) return null;
    return (
      <div key={label} className="mt-2 first:mt-0">
        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--app-text-muted)] border-y border-[var(--border-subtle)] bg-[var(--surface-1)]">
          {label}
        </div>
        {options.map((p) => (
          <button
            key={p.id}
            type="button"
            role="option"
            aria-selected={value === p.id}
            className={optionButtonClass(value === p.id, true)}
            onClick={() => handleSelect(p.id)}
          >
            {p.name || p.id}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className={`
          w-full flex items-center justify-between border border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--app-text)] font-bold tracking-widest uppercase transition-all shadow-[2px_2px_0px_0px_var(--border-strong)]
          ${sizeClass}
          hover:-translate-x-px hover:-translate-y-px hover:shadow-[3px_3px_0px_0px_var(--border-strong)]
          ${isOpen ? 'border-[var(--accent-strong)]' : ''}
        `}
      >
        <span className="truncate">{selectedLabel || placeholder}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute z-[200] w-full mt-2 bg-[var(--surface-2)] border border-[var(--border-strong)] shadow-[4px_4px_0_0_var(--border-strong)] max-h-80 overflow-y-auto min-w-max"
        >
          {showSearch && (
            <div className="p-2 border-b border-[var(--border-subtle)] sticky top-0 bg-[var(--surface-2)]">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pipelines..."
                aria-label="Search pipelines"
                className="input input-sm w-full"
              />
            </div>
          )}
          <div className="py-1">
            {visibleExtras.map((entry) => (
              <button
                key={`extra-${entry.value}`}
                type="button"
                role="option"
                aria-selected={value === entry.value}
                className={optionButtonClass(value === entry.value, false)}
                onClick={() => handleSelect(entry.value)}
              >
                {entry.label}
              </button>
            ))}
            {renderGroup('Built-in', visibleBuiltIns)}
            {renderGroup('My pipelines', visibleMine)}
            {flatOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted">No pipelines found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineSelect;
